import { type File, useStores } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { type KeyboardEvent } from 'react';

import { useInViewport } from '@/hooks/useInViewport';
import { downloadFileWithFeedback } from '@/utils/file-io';
import { getDisplayFilename, stripFileExtension } from '@/utils/files';

import { renderPaperChild } from './artifact-paper-content';
import {
  artifactPolicy,
  canPreview,
  paperShowsFile,
} from './artifact-presentation';
import {
  ArtifactCardShell,
  ArtifactCardTitle,
  ArtifactCardTitleSkeleton,
  artifactTypeLabel,
} from './ArtifactCardShell';
import { ArtifactPaper } from './ArtifactPaper';
import { useArtifactContent } from './useArtifactContent';

interface ArtifactCardProps {
  file: File;
  onOpen?: (file: File) => void;
}

export const ArtifactCard = observer(({ file, onOpen }: ArtifactCardProps) => {
  const { files: filesStore } = useStores();
  const { ref, inViewport } = useInViewport<HTMLDivElement>();

  const displayName = getDisplayFilename(file.filename);
  const nameWithoutExtension = stripFileExtension(displayName);
  const policy = artifactPolicy(file);
  const { renderer, paper } = policy;
  const rendersContent = paperShowsFile(policy);
  const { status, text, url, canvas, title, versionKey } = useArtifactContent(
    file,
    { surface: 'card', enabled: inViewport, canvasTitleProbe: true }
  );
  const isCanvas = renderer === 'canvas';
  const typeLabel = artifactTypeLabel(file.contentType, file.filename);

  const canOpen = canPreview(policy) && onOpen !== undefined;
  const isLoading =
    rendersContent && (status === 'idle' || status === 'loading');
  const isAwaitingTitle = isCanvas && !title && isLoading;

  const download = () =>
    void downloadFileWithFeedback(filesStore, file, 'ArtifactCard');

  const activate = () => {
    if (canOpen) onOpen(file);
    else download();
  };

  return (
    <ArtifactCardShell
      ref={ref}
      onClick={activate}
      role="button"
      tabIndex={0}
      onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activate();
        }
      }}
      aria-label={canOpen ? `Open ${displayName}` : `Download ${displayName}`}
      className="group cursor-pointer transition-colors hover:bg-surface-hover"
    >
      {isAwaitingTitle ? (
        <ArtifactCardTitleSkeleton />
      ) : (
        <ArtifactCardTitle
          title={title ?? nameWithoutExtension}
          subtitle={isCanvas ? 'Artifact' : typeLabel}
        />
      )}
      {paper && (
        <ArtifactPaper isLoading={isLoading} skeleton={paper}>
          {inViewport && rendersContent && status === 'ready'
            ? renderPaperChild(renderer, {
                text,
                url,
                versionKey,
                canvasParseResult: canvas,
              })
            : null}
        </ArtifactPaper>
      )}
    </ArtifactCardShell>
  );
});
